/**
 * Стартовая заглушка: то, что человек видит, пока React ещё не отрисовал.
 *
 * Проблема, которую чиним (замер прода 13.09 на /bots/trends):
 *   HTML с готовой разметкой приезжает за 58 мс — у `/bots/*` внутри #root
 *   лежит 36–386 КБ пререндера. Но `createRoot()` ОЧИЩАЕТ контейнер перед
 *   отрисовкой, и всё это стирается до первого кадра. Первый пиксель появлялся
 *   на 408 мс даже с тёплым кэшем; на холодном и на телефоне человек ждал
 *   1–5 секунд, глядя в пустой экран, — разбор бандла 2,97 МБ.
 *
 * Решение: перед createRoot переносим содержимое #root в отдельный слой
 * `#boot-shell` и прячем сам #root. React спокойно собирается в скрытом
 * контейнере, а на экране всё это время настоящая страница. Как только React
 * отрисовал первый кадр — слой снимается, #root показывается.
 *
 * Что именно лежит в слое:
 *   • на `/bots/*` — пререндер-снапшот, то есть НАСТОЯЩИЙ контент;
 *   • на остальных — скелетон из index.html (у `/`, `/generate`, `/assistants`
 *     снапшота на проде нет).
 *
 * Слой намеренно НЕ интерактивен (это статическая разметка без обработчиков),
 * зато ссылки в нём — настоящие `<a>`: клик до готовности JS уходит обычной
 * навигацией, а не проваливается в пустоту.
 *
 * Снимается слой, когда в #root НАСТОЯЩЕЕ содержимое, а не первый кадр React (замер 30.09, медленный 4G:
 * готовая страница сменялась серыми заглушками лент, а у вошедшего — пустым экраном проверки входа). Заглушки
 * ожидания помечены атрибутом `data-boot-wait` (стартовый экран App, центрированный Loader ленивых страниц,
 * FeedSkeleton): пока такая в #root есть, на экране остаётся готовая страница.
 */

const SHELL_ID = 'boot-shell'
const BOOTING = 'booting'
/** Метка заглушки «страница ещё грузится»: пока она в #root, стартовый слой не снимаем. */
export const BOOT_WAIT_ATTR = 'data-boot-wait'
/** Страховка: дольше этого готовую страницу не держим — упал запрос данных или заглушка так и не ушла. */
const MAX_HOLD_MS = 8000
/** После снятия слоя «страница догружена» наступает с событием load — но не позже этого срока: одна
 *  зависшая картинка не должна навсегда откладывать ролики и подсказки. */
const SETTLE_CAP_MS = 10000

const shellWaiters: Array<() => void> = []

/**
 * Снапшот — от ЭТОГО адреса?
 *
 * index.html служит сразу двум делам: это снапшот главной и запасной файл для
 * всех адресов без своего снапшота. Из-за этого /generate, /assistants и
 * /bots/video|slides|tools открывались с разметкой ГЛАВНОЙ (замер 13.09).
 * Показать её человеку — хуже, чем показать пустоту: он видит не ту страницу,
 * на которую шёл. Клеймо ставит пререндер (scripts/prerender.mjs).
 *
 * Клейма нет вовсе — это скелетон из index.html, он подходит любому адресу.
 */
function shellFitsRoute(): boolean {
	const stamped = document.documentElement.getAttribute('data-prerender-route')
	if (!stamped) return true
	const here = location.pathname.replace(/\/+$/, '') || '/'
	return stamped.replace(/\/+$/, '') === here || stamped === here
}

/** Перенести стартовую разметку в отдельный слой. Вызывать ДО createRoot. */
export function captureBootShell(root: HTMLElement): void {
	// Пусто — прятать нечего: показываем #root сразу, иначе получили бы
	// чёрный экран вместо быстрой отрисовки.
	if (!root.firstElementChild) return
	// Чужой снапшот не показываем вовсе: React очистит #root, и человек увидит
	// заглушки секций (FeedSkeleton) своей страницы, а не чужую страницу.
	if (!shellFitsRoute()) return
	const shell = document.createElement('div')
	shell.id = SHELL_ID
	shell.setAttribute('aria-hidden', 'true')
	while (root.firstChild) shell.appendChild(root.firstChild)
	root.parentNode?.insertBefore(shell, root)
	document.documentElement.classList.add(BOOTING)
}

/** Снять слой. Вызывать ПОСЛЕ кадра с настоящим содержимым. */
export function releaseBootShell(): void {
	const html = document.documentElement
	if (html.classList.contains(BOOTING)) {
		html.classList.remove(BOOTING)
		document.getElementById(SHELL_ID)?.remove()
	}
	for (const fn of shellWaiters.splice(0)) {
		try {
			fn()
		} catch {
			/* чужой обработчик не мешает остальным */
		}
	}
}

/**
 * Позвать cb, когда страница показана и догружена: стартовый слой снят (или его не было) и прошло событие
 * load (но не позже SETTLE_CAP_MS после снятия слоя), плюс delayMs. Для того, что может подождать, — автоплей
 * роликов, подсказки: на медленном интернете они не должны делить канал и экран с первой загрузкой.
 * После первой загрузки (переходы внутри сайта) условие уже выполнено — cb зовётся через delayMs.
 * Возвращает отмену.
 */
export function whenPageSettled(cb: () => void, delayMs = 0): () => void {
	let cancelled = false
	let fired = false
	const timers: ReturnType<typeof setTimeout>[] = []
	const fire = () => {
		if (cancelled || fired) return
		fired = true
		window.removeEventListener('load', fire)
		timers.push(setTimeout(() => { if (!cancelled) cb() }, delayMs))
	}
	const afterShell = () => {
		if (cancelled) return
		if (document.readyState === 'complete') return fire()
		window.addEventListener('load', fire)
		timers.push(setTimeout(fire, SETTLE_CAP_MS))
	}
	if (document.documentElement.classList.contains(BOOTING)) shellWaiters.push(afterShell)
	else afterShell()
	return () => {
		cancelled = true
		window.removeEventListener('load', fire)
		for (const t of timers) clearTimeout(t)
		const i = shellWaiters.indexOf(afterShell)
		if (i >= 0) shellWaiters.splice(i, 1)
	}
}

/**
 * Снять слой, когда в #root настоящее содержимое: React что-то вставил, и среди этого нет заглушки ожидания
 * (`data-boot-wait`).
 *
 * Сигнал берём из DOM (MutationObserver), а не из useEffect компонента: эффект
 * пришлось бы объявлять в main.tsx, а там его запрещает react-refresh, и это
 * не придирка линтера — компонент в точке входа ломает горячую перезагрузку.
 * Наблюдатель ещё и честнее: он ловит именно появление разметки, а не момент,
 * когда React решил, что смонтировался.
 *
 * Двойной rAF, а не один: первый кадр только ПЛАНИРУЕТ отрисовку, снятие слоя
 * в нём даёт кадр, где старого уже нет, а нового ещё нет — то самое мигание,
 * от которого мы и уходим. Таймеры — страховка: во вкладке в фоне кадров нет
 * вовсе (слой снимается без них через полсекунды после готовности), а заглушка,
 * которая так и не ушла, держит слой не дольше MAX_HOLD_MS.
 */
export function releaseBootShellWhenPainted(root: HTMLElement): void {
	if (typeof window === 'undefined') return
	if (!document.documentElement.classList.contains(BOOTING)) return
	let done = false
	let pending = false
	const finish = () => {
		if (done) return
		done = true
		obs.disconnect()
		releaseBootShell()
	}
	const ready = () => !!root.firstElementChild && !root.querySelector(`[${BOOT_WAIT_ATTR}]`)
	const check = () => {
		if (done || pending || !ready()) return
		pending = true
		requestAnimationFrame(() => requestAnimationFrame(finish))
		setTimeout(finish, 500)
	}
	const obs = new MutationObserver(check)
	obs.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: [BOOT_WAIT_ATTR] })
	check()
	setTimeout(finish, MAX_HOLD_MS)
}
